import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Search, HelpCircle, Book, MessageCircle, ArrowLeft, Twitter, Instagram, Facebook, Github, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ThemeLogo } from '../components/ui/theme-logo';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();

  // Function to scroll to a specific section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };
  
  // Force scroll to top when this route is accessed
  useEffect(() => {
    // IMMEDIATE scroll - no delays
    const immediateScroll = () => {
      // Multi-method immediate scroll
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also scroll any potential scrollable containers immediately
      const scrollableElements = Array.from(document.querySelectorAll('*')).filter(el => {
        const style = getComputedStyle(el);
        return style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto';
      });
      
      scrollableElements.forEach(el => {
        if (el.scrollTop > 0) {
          el.scrollTop = 0;
        }
      });
    };
    
    // Execute immediately
    immediateScroll();
    
    // Backup attempts with minimal delay
    requestAnimationFrame(immediateScroll);
    setTimeout(immediateScroll, 1);
    setTimeout(immediateScroll, 10);
    
    // One final check after very short delay to ensure React rendered
    setTimeout(immediateScroll, 50);
  }, [location.pathname]); // Trigger on route change
  
  const faqCategories = [
    {
      title: 'Getting Started',
      icon: <Book className="h-6 w-6 text-primary" />,
      questions: [
        { 
          question: 'How do I add items to my inventory?', 
          answer: 'You can add items to your inventory by clicking the "+" button on the Inventory page, or by using the camera icon to scan your items directly.' 
        },
        { 
          question: 'How does the expiry date tracking work?', 
          answer: 'Nosh tracks the expiry dates of your food items and sends you notifications when items are about to expire. You can set the expiry date when adding items to your inventory.' 
        },
        { 
          question: 'Can I scan receipts to add multiple items?', 
          answer: 'Yes! Nosh allows you to scan your grocery receipts to automatically add multiple items to your inventory at once.' 
        }
      ]
    },
    {
      title: 'AI Features',
      icon: <HelpCircle className="h-6 w-6 text-primary" />,
      questions: [
        { 
          question: 'How does the AI recipe generation work?', 
          answer: 'Our AI analyzes your current inventory and suggests recipes based on what you have available. It prioritizes items that are about to expire and considers your dietary preferences.' 
        },
        { 
          question: 'How accurate is the meal scanning feature?', 
          answer: 'The meal scanning feature uses advanced AI to analyze your meals and provide nutritional information. It typically has an accuracy of 85-95% depending on the clarity of the image and visibility of the food items.' 
        },
        { 
          question: 'Can I customize the AI recipe suggestions?', 
          answer: 'Yes, you can set preferences like cuisine type, meal type, cooking time, and dietary restrictions to get personalized recipe suggestions.' 
        }
      ]
    },
    {
      title: 'Account & Settings',
      icon: <MessageCircle className="h-6 w-6 text-primary" />,
      questions: [
        { 
          question: 'How do I change my password?', 
          answer: 'You can change your password by going to the Profile page and clicking on the "Security" tab.' 
        },
        { 
          question: 'Is my data secure?', 
          answer: 'Yes, we use industry-standard encryption and security practices to protect your data. Your information is never shared with third parties without your consent.' 
        },
        { 
          question: 'How do I delete my account?', 
          answer: 'You can delete your account by going to the Profile page, clicking on "Settings", and selecting "Delete Account". Please note that this action is irreversible.' 
        }
      ]
    }
  ];

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(faq =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-white dark:from-background dark:to-gray-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-background to-white dark:from-background dark:to-gray-950 shadow-md py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center">
              <ThemeLogo className="h-[42px]" />
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/#features" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Features</Link>
              <Link to="/#how-it-works" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">How It Works</Link>
              <Link to="/#community" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Community</Link>
              <Link to="/#pricing" className="text-gray-600 dark:text-gray-300 hover:text-primary transition-colors">Pricing</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/auth/login">
                <Button variant="ghost" className="hidden sm:flex">Sign in</Button>
              </Link>
              <Link to="/auth/login">
                <Button className="bg-primary hover:bg-primary-hover text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Link to="/" className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-primary transition-colors mb-6">
              <ArrowLeft size={16} className="mr-2" />
              Back to Home
            </Link>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Help <span className="text-primary">Center</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              Find answers to common questions and learn how to make the most of Nosh.
            </motion.p>
          </div>

          {/* Search Bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="max-w-2xl mx-auto mb-16"
          >
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 py-4 text-lg rounded-2xl border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Categories */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Popular Topics */}
          {searchQuery === '' && (
            <div className="mb-16">
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-3xl font-bold text-gray-900 dark:text-white text-center mb-12"
              >
                Popular Topics
              </motion.h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {faqCategories.map((category, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow"
                  >
                    <button 
                      onClick={() => scrollToSection(`section-${category.title.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}`)}
                      className="flex items-center mb-6 w-full text-left hover:opacity-80 transition-opacity"
                    >
                      <div className="p-3 bg-primary/10 rounded-xl mr-4">
                        {category.icon}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{category.title}</h3>
                    </button>
                    <ul className="space-y-4">
                      {category.questions.slice(0, 3).map((faq, faqIndex) => (
                        <li key={faqIndex}>
                          <button 
                            onClick={() => scrollToSection(`faq-${category.title.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}-${faqIndex}`)}
                            className="flex items-center justify-between w-full text-left hover:text-primary transition-colors"
                          >
                            <span className="text-sm text-gray-600 dark:text-gray-400">{faq.question}</span>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* FAQ Sections */}
          <div className="space-y-12">
            {(searchQuery ? filteredCategories : faqCategories).map((category, index) => (
              <div 
                key={index} 
                id={`section-${category.title.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}`}
                className="space-y-6"
              >
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-2xl font-bold text-gray-900 dark:text-white flex items-center scroll-mt-24"
                >
                  <div className="p-3 bg-primary/10 rounded-xl mr-4">
                    {category.icon}
                  </div>
                  {category.title}
                </motion.h2>
                <div className="grid gap-6">
                  {category.questions.map((faq, faqIndex) => (
                    <motion.div
                      key={faqIndex}
                      id={`faq-${category.title.toLowerCase().replace(/\s+/g, '-').replace('&', 'and')}-${faqIndex}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: (index * 0.1) + (faqIndex * 0.05) }}
                      className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 scroll-mt-24"
                    >
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{faq.question}</h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{faq.answer}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {searchQuery && filteredCategories.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center py-16"
            >
              <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-6">
                <Search className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No results found</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                We couldn't find any articles matching "{searchQuery}". Try a different search term.
              </p>
              <Button onClick={() => setSearchQuery('')} variant="outline">
                Clear Search
              </Button>
            </motion.div>
          )}

          {/* Contact Support */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-20 bg-primary/5 rounded-2xl p-12 border border-primary/20 text-center"
          >
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Still need help?</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
              Our support team is always ready to assist you with any questions or issues you might have about reducing food waste with Nosh.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-primary hover:bg-primary-hover text-white px-8 py-3 rounded-xl">
                Contact Support
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16 mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img src="/nosh-new-wh.png" alt="Nosh" className="h-[42px]" />
              </Link>
              <p className="text-gray-400 mb-6 max-w-md">
                Reduce food waste with AI-powered inventory management, recipe suggestions, and meal tracking.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Twitter size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Instagram size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Facebook size={20} />
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition-colors">
                  <Github size={20} />
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/#features" className="text-gray-400 hover:text-white transition-colors">Features</Link></li>
                <li><Link to="/#how-it-works" className="text-gray-400 hover:text-white transition-colors">How It Works</Link></li>
                <li><Link to="/#pricing" className="text-gray-400 hover:text-white transition-colors">Pricing</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/help-center" className="text-gray-400 hover:text-white transition-colors">Help Center</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact Us</Link></li>
                <li><Link to="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              © 2025 Nosh. All rights reserved.
            </p>
            <div className="flex items-center mt-4 md:mt-0">
              <Mail size={16} className="mr-2 text-gray-400" />
              <a href="mailto:hello@noshhq.com" className="text-gray-400 hover:text-white transition-colors text-sm">
                hello@noshhq.com
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenter;